import { ButtonFrame, Card, CardSearch, InputDropdown, InputField, PageBodyHeader, Table, UploadField } from "../../component";
import { useEffect, useState } from "react";
import { useParams, useOutletContext } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../redux";
import { checkStoreByStoreIdUserId, createOrderTracker, dateToSystemTz, fetchOrderRequest, fetchOrderTrackerDetail, updateOrderTracker } from "../../action/generalFunc";
import { generalObj } from "../../constant/object_types";
import ModalParent from "../../component/modal/ModalParent";
import { toast } from "react-toastify";

const statusBadgeColor = (status:string) => {
    switch (status.toLowerCase()) {
        case "fulfilled":
            return { backgroundColor: "#E4F7EA", color: "#00983A" };
        case "cancelled":
            return { backgroundColor: "#FCE4E4", color: "#EB212F" };
        case "pending":
            return { backgroundColor: "#FFF3D6", color: "#B5820B" };
        default:
            return { backgroundColor: "#E9EAEB", color: "#676967" };
    }
};

const StaffOrderTrackerDetail = () => {
    const { id, store_id, date } = useParams();
    const account = useSelector((state:RootState) => state.account);
    const { setModalLoading }: { setModalLoading:Function } = useOutletContext();

    const [searchText, setSearchText] = useState<string>("");
    const [storeName, setStoreName] = useState<string>("");
    const [vendorName, setVendorName] = useState<string>("");
    const [orderTrackerDetails, setOrderTrackerDetails] = useState<generalObj[]>([]);
    const [orderRequests, setOrderRequests] = useState<generalObj[]>([]);
    const [openTrackerModal, setOpenTrackerModal] = useState<boolean>(false);
    const [formData, setFormData] = useState<generalObj>({ order_request: null, po_number: "", order_date: "", order_value: "", po_file: null, document_url: "" });
    const [fulfillOrderId, setFulfillOrderId] = useState<number | string | null>(null);
    const [fulfillmentDate, setFulfillmentDate] = useState<string>("");
    const [fulfillmentValue, setFulfillmentValue] = useState<string>("");
    const [openFulfillModal, setOpenFulfillModal] = useState<boolean>(false);

    useEffect(() => {
        if (!account || !account.isAuthenticated || !id || !store_id) return;

        const loadForm = async () => {
            setModalLoading(true);
            const storeResult = await checkStoreByStoreIdUserId(account.token, store_id, id);
            if (!storeResult) {
                window.history.back();
                setModalLoading(false);
                return;
            }

            setStoreName(`${storeResult.data.store_data.name} - ${storeResult.data.store_data.organization}`);
            setVendorName(storeResult.data.vendor_data.username);
            const [orderRequestResult, trackerDetailResult] = await Promise.all([
                fetchOrderRequest(account.token, store_id, id, false),
                fetchOrderTrackerDetail(account.token, id, store_id, date || "")
            ]);
            setOrderRequests(orderRequestResult || []);
            setOrderTrackerDetails(trackerDetailResult || []);
            
            setModalLoading(false);
        };

        loadForm();
    }, [account, id, store_id, date, setModalLoading]);
        
    const formatMonth = (month:string|undefined) => {
        if (!month) return "-";
        const [year, monthNumber] = month.split("_");
        const monthDate = new Date(Number(year), Number(monthNumber) - 1);
        return Number.isNaN(monthDate.getTime()) ? month : `${monthDate.toLocaleString("en-US", {month:"short"})} ${year}`;
    };

    const formatNumber = (value:number | string | undefined | null) => value === undefined || value === null ? "-" : Number(value).toLocaleString("en-US", {maximumFractionDigits: 2});

    const updateFormData = (key:string, value:any) => {
        setFormData((previousData) => ({...previousData, [key]:value}));
    };

    const pickPoDocument = (event:generalObj, isDrag:boolean=false) => {
        if (!isDrag && !event.target.files.length) return;
        const file = isDrag ? event : event.target.files[0];
        if (!file || !["application/pdf", "image/png", "image/jpeg", "image/jpg"].includes(file.type)) {
            toast("Only PDF, PNG, or JPEG files are allowed");
            return;
        }
        if (Math.floor(file.size / 1000) > 1024) {
            toast("Document size cannot exceed 1mb");
            return;
        }
        updateFormData("po_file", file);
    };

    const submitOrderTracker = async () => {
        if (!account || !account.isAuthenticated || !id || !store_id) return;
        if (!formData.order_request || !formData.po_number.trim() || !formData.order_date || formData.order_value === "") {
            toast("Order request number, PO number, order date, and order value are required");
            return;
        }
        if (Number(formData.order_value) < 0 || Number.isNaN(Number(formData.order_value))) {
            toast("Order value must be a valid amount");
            return;
        }

        setModalLoading(true);
        const result = await createOrderTracker(account.token, {
            order_request: formData.order_request.id,
            po_number: formData.po_number.trim(),
            order_date: formData.order_date,
            order_value: Number(formData.order_value),
            po_file: formData.po_file,
            vendor: id,
            store: store_id
        });
        if (result) {
            toast(result);
            const trackerDetailResult = await fetchOrderTrackerDetail(account.token, id, store_id, date || "");
            setOrderTrackerDetails(trackerDetailResult || []);
            setFormData({ order_request: null, po_number: "", order_date: "", order_value: "", po_file: null, document_url: "" });
            setOpenTrackerModal(false);
        }
        setModalLoading(false);
    };

    const openFulfillmentModal = (order:generalObj) => {
        setFulfillOrderId(order.id);
        setFulfillmentDate(new Date().toISOString().slice(0, 10));
        setFulfillmentValue("");
        setOpenFulfillModal(true);
    };

    const submitFulfillment = async (orderId:number|string|null) => {

        if (!account || !account.isAuthenticated || !id || !store_id || !orderId) return;
        if (!fulfillmentDate) {
            toast("Fulfillment date is required");
            return;
        }
        if (fulfillmentValue === "" || Number(fulfillmentValue) < 0 || Number.isNaN(Number(fulfillmentValue))) {
            toast("Fulfillment value must be a valid amount");
            return;
        }

        setModalLoading(true);
        const result = await updateOrderTracker(account.token, {
            id: orderId,
            status: "fulfilled",
            fulfillment_date: fulfillmentDate,
            fulfilled_order_value: Number(fulfillmentValue)
        });
        if (result) {
            toast(result);
            const trackerDetailResult = await fetchOrderTrackerDetail(account.token, id, store_id, date || "");
            setOrderTrackerDetails(trackerDetailResult || []);
            setOpenFulfillModal(false);
        }
        setModalLoading(false);
    };

    const trackedOrderRequestIds = orderTrackerDetails.map((tracker) => tracker.order_request?.id || tracker.order_request_id || tracker.order_request).filter(Boolean);
    const availableOrderRequests = orderRequests.filter((order) => !trackedOrderRequestIds.includes(order.id)).map((order, index) => ({
        ...order,
        label: `#${order.request_number || order.order_number || order.order_request_number || order.id || index + 1}`
    }));

    return (
        <>
            <PageBodyHeader title={`Order Tracker for ${formatMonth(date)}`} desc="Track your store's order requests for a specific date"
                inlineChild={<ButtonFrame btn_text="Back" btn_style={{padding:"8px 24px", borderRadius:"8px", border:"1px solid #808280",  backgroundColor:"#808280", color:"#FFF", fontSize:"14px", fontWeight:"600", letterSpacing:"-0.4px"}} onPress={() => window.history.back()} />}
            >
                <ButtonFrame btn_text="New Order Tracker" btn_style={{padding:"10px 18px", borderRadius:"8px", backgroundColor:"#1159AB", color:"#FFF", fontSize:"14px", fontWeight:"600"}} onPress={() => setOpenTrackerModal(true)} />
            </PageBodyHeader>

            <ModalParent visible={openTrackerModal}>
                <div className="ViewContainer">
                    <div className="input__pop" style={{maxWidth:"620px", width:"90vw"}}>
                        <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"24px"}}>
                            <h4>New Order Tracker</h4>
                            <button type="button" title="Close" onClick={() => setOpenTrackerModal(false)} style={{fontSize:"22px", padding:"4px 10px"}}>&times;</button>
                        </div>
                        <Card color="#676967" containerCardStyle={{overflow:"auto"}}>
                            <div style={{display:"grid", gap:"20px"}}>
                                <div style={{display:"grid", gap:"20px", maxHeight:"350px", overflowX:"hidden"}}>
                                    <InputDropdown label="Order Request Number *" placeHolder="Select order request" data={availableOrderRequests} selectedData={formData.order_request} onItemChange={(order) => updateFormData("order_request", order)} containerStyle={{width:"100%"}} />
                                    <InputField label="PO Number *" inputProp={{value:formData.po_number, placeholder:"Enter PO number"}} onTextChange={(value) => updateFormData("po_number", value)} containerStyle={{width:"100%"}} />
                                    <InputField label="Order Date *" inputProp={{value:formData.order_date, type:"date"}} onTextChange={(value) => updateFormData("order_date", value)} containerStyle={{width:"100%"}} />
                                    <InputField label="Order Value *" inputProp={{value:formData.order_value, placeholder:"Enter order value", type:"number", min:"0", step:"0.01"}} onTextChange={(value) => updateFormData("order_value", value)} containerStyle={{width:"100%"}} />
                                    <UploadField label="PO Document / Image (optional)" current_obj={formData.po_file} current_obj_url={formData.document_url} upload_description="Drag and drop a PDF or image (Max. 1mb)" file_types="application/pdf,image/png,image/jpeg,image/jpg" containerStyle={{width:"100%"}} inputContainerStyle={{height:"150px"}} onSelectFile={pickPoDocument} onInputClear={() => setFormData((previousData) => ({...previousData, po_file:null, document_url:""}))} />
                                </div>
                                <div style={{display:"flex", alignItems:"center", gap:"20px", marginTop:"12px"}}>
                                    <ButtonFrame btn_text="Save Tracker" btn_style={{padding:"12px 24px", borderRadius:"8px", backgroundColor:"#00983A", color:"#FFF", fontSize:"14px", fontWeight:"600"}} onPress={submitOrderTracker} />
                                    <ButtonFrame btn_text="Cancel" btn_style={{padding:"12px 24px", borderRadius:"8px", border:"1px solid #808280", color:"#808280", fontSize:"14px", fontWeight:"600"}} onPress={() => setOpenTrackerModal(false)} />
                                </div>
                            </div>
                        </Card>
                    </div>
                </div>
            </ModalParent>

            <div style={{marginTop:"30px"}}>
                <Table table_head={["#", "Order Date", "Order request ID", "PO Number", "Total Value", "Fulfilled Value", "Fulfillment Date", "Status", "Action"]} th_class="white" color="#1159AB" thColor="#1159AB" tableSearch={<CardSearch value={searchText} onTextChange={(value) => setSearchText(value)} />} table_title={`Order requests for ${vendorName.toUpperCase()} in ${storeName}`}>
                    <>
                        {orderTrackerDetails.filter((order) => `${order.order_request?.order_request_number} ${order.po_number} ${order.status}`.toLowerCase().includes(searchText.toLowerCase())).map((order, index) => (
                            <tr key={order.id || index}>
                                <td>{index + 1}</td>
                                <td>{dateToSystemTz(order.order_date)}</td>
                                <td>{order.order_request?.order_request_number || "-"}</td>
                                <td>{order.po_number || "-"}</td>
                                <td>{formatNumber(order.order_value)}</td>
                                <td>{formatNumber(order.fulfilled_order_value)}</td>
                                <td>{order.fulfillment_date ? dateToSystemTz(order.fulfillment_date) : "-"}</td>
                                <td><span style={{padding:"4px 12px", borderRadius:"12px", fontSize:"12px", fontWeight:"600", textTransform:"capitalize", ...statusBadgeColor(order.status || "")}}>{order.status || "-"}</span></td>
                                <td>
                                    <div style={{display:"flex", alignItems:"center", gap:"10px"}}>
                                        { order.po_file && (
                                            <div>
                                                <a href={order.po_file} target="_blank" rel="noreferrer">View PO</a>
                                            </div>
                                            )
                                        }
                                        {order.status.toLowerCase() === "fulfilled" ? null : <button type="button" onClick={() => openFulfillmentModal(order)} style={{padding:"6px 12px", borderRadius:"6px", border:"1px solid #00983A", color:"#00983A", fontWeight:"600"}}>Fulfill</button>}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </>
                </Table>
            </div>

            <ModalParent visible={openFulfillModal}>
                <div className="ViewContainer">
                    <div className="input__pop" style={{maxWidth:"700px", width:"90vw"}}>
                        <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"24px"}}>
                            <h4>{`Fulfill Order Tracker #${fulfillOrderId ?? ""}`}</h4>
                            <button type="button" title="Close" onClick={() => setOpenFulfillModal(false)} style={{fontSize:"22px", padding:"4px 10px"}}>&times;</button>
                        </div>
                        <Card color="#676967" containerCardStyle={{overflow:"auto"}}>
                            <div style={{padding:"20px"}}>
                                <div style={{display:"flex", gap:"20px", flexWrap:"wrap"}}>
                                    <InputField label="Fulfillment Date *" inputProp={{value:fulfillmentDate, type:"date"}} onTextChange={setFulfillmentDate} containerStyle={{width:"260px"}} />
                                    <InputField label="Fulfillment Value *" inputProp={{value:fulfillmentValue, placeholder:"Enter fulfillment value", type:"number", min:"0", step:"0.01"}} onTextChange={setFulfillmentValue} containerStyle={{width:"260px"}} />
                                </div>
                                <div style={{marginTop:"32px", display:"flex", alignItems:"center", gap:"20px"}}>
                                    <ButtonFrame btn_text="Submit Fulfillment" btn_style={{padding:"12px 24px", borderRadius:"8px", backgroundColor:"#00983A", color:"#FFF", fontSize:"14px", fontWeight:"600"}} onPress={() => submitFulfillment(fulfillOrderId)} />
                                    <ButtonFrame btn_text="Cancel" btn_style={{padding:"12px 24px", borderRadius:"8px", border:"1px solid #808280", color:"#808280", fontSize:"14px", fontWeight:"600"}} onPress={() => setOpenFulfillModal(false)} />
                                </div>
                            </div>
                        </Card>
                    </div>
                </div>
            </ModalParent>
        </>
    )
}

export default StaffOrderTrackerDetail;